import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react'
import {
  changeMasterPassword,
  createProject,
  createVault,
  encryptVault,
  estimateBits,
  generatePassword,
  unlockVault,
  validateEnvelope,
  validatePolicy,
  type PasswordPolicy,
  type ProjectRecord,
  type VaultEnvelope,
  type VaultPayload,
  type VaultSession,
  type VaultSettings,
} from './core'
import { loadEnvelope, saveEnvelope } from './storage'

type AppMode = 'loading' | 'setup' | 'locked' | 'unlocked'
type MobileView = 'projects' | 'workbench'

function BrandMark() {
  return (
    <svg className="brand-mark" viewBox="0 0 48 48" aria-hidden="true">
      <rect x="5" y="8" width="38" height="23" rx="3" />
      <path d="M11 31v9h6v-5h6v5h6v-5h6v5h4v-9" />
      <circle cx="17" cy="19.5" r="3" />
      <path d="M22 19.5h14" />
    </svg>
  )
}

function AccessScreen({
  hasVault,
  onCreate,
  onUnlock,
  onImport,
}: {
  hasVault: boolean
  onCreate: (masterPassword: string) => Promise<void>
  onUnlock: (masterPassword: string) => Promise<void>
  onImport: (file: File) => Promise<void>
}) {
  const [master, setMaster] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError('')
    if (!hasVault && master !== confirmation) {
      setError('两次输入的主密码不一致。')
      return
    }
    setBusy(true)
    try {
      await (hasVault ? onUnlock(master) : onCreate(master))
      setMaster('')
      setConfirmation('')
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '操作失败，请重试。')
    } finally {
      setBusy(false)
    }
  }

  async function importFile(file?: File) {
    if (!file) return
    setError('')
    setBusy(true)
    try {
      await onImport(file)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '无法导入备份。')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="access-shell">
      <section className="access-panel" aria-labelledby="access-title">
        <div className="access-brand">
          <BrandMark />
          <div>
            <p className="eyebrow">LOCAL-FIRST PASSWORD WORKBENCH</p>
            <h1 id="access-title">Pass Creator</h1>
          </div>
        </div>
        <p className="access-intro">
          {hasVault
            ? '使用主密码解锁本机保险库。主密码不会保存，也不会发送到网络。'
            : '创建一个只属于这台设备的加密保险库。请先准备一个至少 12 个字符的主密码。'}
        </p>
        <form className="access-form" onSubmit={submit}>
          <label htmlFor="master-password">主密码</label>
          <div className="password-input">
            <input
              id="master-password"
              type={show ? 'text' : 'password'}
              autoComplete={hasVault ? 'current-password' : 'new-password'}
              value={master}
              onChange={(event) => setMaster(event.target.value)}
              minLength={12}
              required
              autoFocus
            />
            <button type="button" className="text-button" onClick={() => setShow((value) => !value)}>
              {show ? '隐藏' : '显示'}
            </button>
          </div>
          {!hasVault && (
            <>
              <label htmlFor="master-confirmation">再次输入主密码</label>
              <input
                id="master-confirmation"
                type={show ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                minLength={12}
                required
              />
            </>
          )}
          {error && <p className="field-error" role="alert">{error}</p>}
          <button className="primary-button" type="submit" disabled={busy}>
            {busy ? '处理中…' : hasVault ? '解锁保险库' : '创建保险库'}
          </button>
        </form>
        <div className="access-divider"><span>或</span></div>
        <label className="file-button">
          导入加密备份
          <input type="file" accept="application/json,.json" onChange={(event) => importFile(event.target.files?.[0])} />
        </label>
        <p className="privacy-note">离线可用 · 无账号 · 无分析 · 数据仅保存在浏览器中</p>
      </section>
      <aside className="access-calibration" aria-label="安全模型摘要">
        <span className="calibration-number">01</span>
        <h2>主密码只负责开锁</h2>
        <p>独立的生成种子保存在加密保险库内，更换主密码不会改变已经生成的项目密码。</p>
        <div className="calibration-rule" />
        <span className="calibration-number">02</span>
        <h2>规则可复现</h2>
        <p>项目、账户、规则和轮换计数相同，结果就相同。需要新密码时，只需增加计数。</p>
      </aside>
    </main>
  )
}

function Modal({ title, onClose, children, wide = false }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    const dialog = ref.current
    if (dialog && !dialog.open) dialog.showModal()
    return () => dialog?.close()
  }, [])

  return (
    <dialog ref={ref} className={wide ? 'modal modal-wide' : 'modal'} onCancel={(event) => { event.preventDefault(); onClose() }}>
      <div className="modal-heading">
        <h2>{title}</h2>
        <button className="icon-button" type="button" aria-label="关闭" onClick={onClose}>×</button>
      </div>
      {children}
    </dialog>
  )
}

function ProjectDialog({ project, onClose, onSave }: { project?: ProjectRecord; onClose: () => void; onSave: (project: ProjectRecord) => Promise<void> }) {
  const [name, setName] = useState(project?.name ?? '')
  const [account, setAccount] = useState(project?.account ?? '')
  const [website, setWebsite] = useState(project?.website ?? '')
  const [notes, setNotes] = useState(project?.notes ?? '')
  const [tags, setTags] = useState(project?.tags.join(', ') ?? '')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!name.trim()) {
      setError('请输入项目名称。')
      return
    }
    setBusy(true)
    const base = project ?? createProject(name, account)
    try {
      await onSave({
        ...base,
        name: name.trim(),
        account: account.trim(),
        website: website.trim(),
        notes: notes.trim(),
        tags: [...new Set(tags.split(',').map((tag) => tag.trim()).filter(Boolean))].slice(0, 12),
        updatedAt: new Date().toISOString(),
      })
      onClose()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '无法保存项目。')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title={project ? '编辑项目' : '新建项目'} onClose={onClose}>
      <form className="stack-form" onSubmit={submit}>
        <label htmlFor="project-name">项目名称</label>
        <input id="project-name" value={name} onChange={(event) => setName(event.target.value)} maxLength={80} required autoFocus />
        <label htmlFor="project-account">账户标识</label>
        <input id="project-account" value={account} onChange={(event) => setAccount(event.target.value)} maxLength={120} placeholder="例如邮箱或用户名" />
        <label htmlFor="project-website">网站</label>
        <input id="project-website" type="url" value={website} onChange={(event) => setWebsite(event.target.value)} maxLength={300} placeholder="https://example.com" />
        <label htmlFor="project-tags">标签</label>
        <input id="project-tags" value={tags} onChange={(event) => setTags(event.target.value)} maxLength={240} placeholder="工作, 云服务" />
        <label htmlFor="project-notes">备注</label>
        <textarea id="project-notes" value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={1000} rows={4} />
        {error && <p className="field-error" role="alert">{error}</p>}
        <div className="form-actions">
          <button type="button" className="secondary-button" onClick={onClose}>取消</button>
          <button type="submit" className="primary-button" disabled={busy}>{busy ? '保存中…' : '保存项目'}</button>
        </div>
      </form>
    </Modal>
  )
}

function RuleEditor({ project, onSave }: { project: ProjectRecord; onSave: (policy: PasswordPolicy) => Promise<void> }) {
  const [policy, setPolicy] = useState(project.policy)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => setPolicy(project.policy), [project.id, project.updatedAt, project.policy])
  const dirty = JSON.stringify(policy) !== JSON.stringify(project.policy)

  async function submit(event: FormEvent) {
    event.preventDefault()
    const issue = validatePolicy(policy)
    setError(issue)
    if (issue) return
    setBusy(true)
    try {
      await onSave(policy)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '无法保存规则。')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="rule-editor" onSubmit={submit}>
      <div className="rule-length">
        <label htmlFor="password-length">长度</label>
        <input
          id="password-length"
          type="range"
          min="12"
          max="64"
          value={policy.length}
          onChange={(event) => setPolicy({ ...policy, length: Number(event.target.value) })}
        />
        <output htmlFor="password-length">{policy.length}</output>
      </div>
      <fieldset className="rule-grid">
        <legend>字符类型</legend>
        {([
          ['lowercase', '小写字母'],
          ['uppercase', '大写字母'],
          ['digits', '数字'],
          ['symbols', '符号'],
        ] as const).map(([key, label]) => (
          <label className="check-control" key={key}>
            <input type="checkbox" checked={policy[key]} onChange={(event) => setPolicy({ ...policy, [key]: event.target.checked })} />
            <span>{label}</span>
          </label>
        ))}
      </fieldset>
      <details className="advanced-rules">
        <summary>高级排除规则</summary>
        <label className="check-control">
          <input type="checkbox" checked={policy.excludeAmbiguous} onChange={(event) => setPolicy({ ...policy, excludeAmbiguous: event.target.checked })} />
          <span>排除易混淆字符 I、l、1、O、0、o</span>
        </label>
        <label htmlFor="custom-exclusions">自定义排除字符</label>
        <input id="custom-exclusions" value={policy.customExclusions} onChange={(event) => setPolicy({ ...policy, customExclusions: event.target.value })} maxLength={128} />
      </details>
      {error && <p className="field-error" role="alert">{error}</p>}
      <button className="secondary-button save-rule" type="submit" disabled={!dirty || busy}>{busy ? '保存中…' : dirty ? '保存规则' : '规则已保存'}</button>
    </form>
  )
}

function SettingsDialog({
  settings,
  envelope,
  onClose,
  onSave,
  onImport,
  onChangeMaster,
}: {
  settings: VaultSettings
  envelope: VaultEnvelope
  onClose: () => void
  onSave: (settings: VaultSettings) => Promise<void>
  onImport: (file: File) => Promise<void>
  onChangeMaster: (password: string) => Promise<void>
}) {
  const [draft, setDraft] = useState(settings)
  const [newMaster, setNewMaster] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)

  async function saveSettings(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setMessage('')
    try {
      await onSave(draft)
      setMessage('设置已保存。')
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : '无法保存设置。')
    } finally {
      setBusy(false)
    }
  }

  function exportBackup() {
    const blob = new Blob([JSON.stringify(envelope, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `pass-creator-${new Date().toISOString().slice(0, 10)}.json`
    link.click()
    URL.revokeObjectURL(url)
  }

  async function changeMaster(event: FormEvent) {
    event.preventDefault()
    setMessage('')
    if (newMaster !== confirmation) {
      setMessage('两次输入的新主密码不一致。')
      return
    }
    setBusy(true)
    try {
      await onChangeMaster(newMaster)
      setNewMaster('')
      setConfirmation('')
      setMessage('主密码已更换，项目密码保持不变。')
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : '无法更换主密码。')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title="保险库设置" onClose={onClose} wide>
      <div className="settings-grid">
        <form className="settings-section stack-form" onSubmit={saveSettings}>
          <div><p className="section-kicker">界面与锁定</p><h3>日常行为</h3></div>
          <label htmlFor="theme">主题</label>
          <select id="theme" value={draft.theme} onChange={(event) => setDraft({ ...draft, theme: event.target.value as VaultSettings['theme'] })}>
            <option value="system">跟随系统</option>
            <option value="light">浅色</option>
            <option value="dark">深色</option>
          </select>
          <label htmlFor="auto-lock">自动锁定</label>
          <select id="auto-lock" value={draft.autoLockMinutes} onChange={(event) => setDraft({ ...draft, autoLockMinutes: Number(event.target.value) })}>
            <option value="1">1 分钟</option><option value="5">5 分钟</option><option value="15">15 分钟</option><option value="30">30 分钟</option>
          </select>
          <label htmlFor="clipboard-clear">尝试清空剪贴板</label>
          <select id="clipboard-clear" value={draft.clearClipboardSeconds} onChange={(event) => setDraft({ ...draft, clearClipboardSeconds: Number(event.target.value) })}>
            <option value="0">不处理</option><option value="15">15 秒后</option><option value="30">30 秒后</option><option value="60">60 秒后</option>
          </select>
          <p className="helper-text">浏览器可能拒绝读取或覆盖剪贴板，因此清空操作只能尽力执行。</p>
          <button className="primary-button" disabled={busy}>保存设置</button>
        </form>
        <section className="settings-section">
          <p className="section-kicker">迁移与恢复</p><h3>加密备份</h3>
          <p>备份包含恢复项目密码所需的生成种子，但始终保持加密。请把文件和主密码分开保存。</p>
          <div className="button-row">
            <button className="secondary-button" type="button" onClick={exportBackup}>导出备份</button>
            <label className="file-button compact">导入备份<input type="file" accept="application/json,.json" onChange={(event) => event.target.files?.[0] && onImport(event.target.files[0])} /></label>
          </div>
        </section>
        <form className="settings-section stack-form" onSubmit={changeMaster}>
          <div><p className="section-kicker">密钥维护</p><h3>更换主密码</h3></div>
          <label htmlFor="new-master">新主密码</label>
          <input id="new-master" type="password" autoComplete="new-password" minLength={12} value={newMaster} onChange={(event) => setNewMaster(event.target.value)} required />
          <label htmlFor="new-master-confirm">再次输入</label>
          <input id="new-master-confirm" type="password" autoComplete="new-password" minLength={12} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} required />
          <button className="secondary-button" disabled={busy}>更换主密码</button>
        </form>
      </div>
      {message && <p className="settings-message" role="status">{message}</p>}
    </Modal>
  )
}

function safeWebsite(value: string) {
  if (!value) return ''
  try {
    const url = new URL(value)
    return ['http:', 'https:'].includes(url.protocol) ? url.href : ''
  } catch {
    return ''
  }
}

function characterKind(character: string) {
  if (/[a-z]/.test(character)) return 'lower'
  if (/[A-Z]/.test(character)) return 'upper'
  if (/\d/.test(character)) return 'digit'
  return 'symbol'
}

export default function App() {
  const [mode, setMode] = useState<AppMode>('loading')
  const [envelope, setEnvelope] = useState<VaultEnvelope | null>(null)
  const [session, setSession] = useState<VaultSession | null>(null)
  const [selectedId, setSelectedId] = useState('')
  const [search, setSearch] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [projectDialog, setProjectDialog] = useState<'new' | 'edit' | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [mobileView, setMobileView] = useState<MobileView>('workbench')
  const [generated, setGenerated] = useState('')
  const [revealed, setRevealed] = useState(false)
  const [announcement, setAnnouncement] = useState('')
  const [deletedProject, setDeletedProject] = useState<ProjectRecord | null>(null)
  const [updateReady, setUpdateReady] = useState(false)
  const lastActivity = useRef(Date.now())
  const clipboardTimer = useRef<number | undefined>(undefined)

  useEffect(() => {
    loadEnvelope()
      .then((stored) => {
        setEnvelope(stored ?? null)
        setMode(stored ? 'locked' : 'setup')
      })
      .catch(() => setMode('setup'))
  }, [])

  const lock = useCallback(() => {
    setSession(null)
    setGenerated('')
    setRevealed(false)
    setSettingsOpen(false)
    setMode('locked')
    setAnnouncement('保险库已锁定。')
  }, [])

  useEffect(() => {
    if (!session) return
    const touch = () => { lastActivity.current = Date.now() }
    const check = () => {
      if (Date.now() - lastActivity.current >= session.payload.settings.autoLockMinutes * 60_000) lock()
    }
    const events = ['pointerdown', 'keydown', 'touchstart'] as const
    events.forEach((event) => window.addEventListener(event, touch, { passive: true }))
    document.addEventListener('visibilitychange', check)
    const timer = window.setInterval(check, 15_000)
    return () => {
      events.forEach((event) => window.removeEventListener(event, touch))
      document.removeEventListener('visibilitychange', check)
      window.clearInterval(timer)
    }
  }, [lock, session])

  useEffect(() => {
    const theme = session?.payload.settings.theme ?? 'system'
    if (theme === 'system') delete document.documentElement.dataset.theme
    else document.documentElement.dataset.theme = theme
  }, [session?.payload.settings.theme])

  useEffect(() => {
    const ready = () => setUpdateReady(true)
    window.addEventListener('pass-creator:update', ready)
    return () => window.removeEventListener('pass-creator:update', ready)
  }, [])

  const projects = session?.payload.projects ?? []
  const selectedProject = projects.find((project) => project.id === selectedId) ?? projects.find((project) => !project.archived) ?? null

  useEffect(() => {
    if (selectedProject && selectedProject.id !== selectedId) setSelectedId(selectedProject.id)
  }, [selectedId, selectedProject])

  useEffect(() => {
    let cancelled = false
    setRevealed(false)
    if (!session || !selectedProject) {
      setGenerated('')
      return
    }
    generatePassword(session.payload.generationSeed, selectedProject.name, selectedProject.account, selectedProject.counter, selectedProject.policy)
      .then((password) => { if (!cancelled) setGenerated(password) })
      .catch((reason) => { if (!cancelled) setAnnouncement(reason instanceof Error ? reason.message : '无法生成密码。') })
    return () => { cancelled = true }
  }, [selectedProject, session])

  const visibleProjects = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('zh-CN')
    return projects
      .filter((project) => project.archived === showArchived)
      .filter((project) => !query || [project.name, project.account, project.tags.join(' ')].join(' ').toLocaleLowerCase('zh-CN').includes(query))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  }, [projects, search, showArchived])

  async function create(masterPassword: string) {
    const created = await createVault(masterPassword)
    await saveEnvelope(created.envelope)
    setEnvelope(created.envelope)
    setSession(created.session)
    setMode('unlocked')
  }

  async function unlock(masterPassword: string) {
    if (!envelope) throw new Error('没有找到保险库。')
    const opened = await unlockVault(masterPassword, envelope)
    setSession(opened)
    setSelectedId(opened.payload.projects.find((project) => !project.archived)?.id ?? '')
    lastActivity.current = Date.now()
    setMode('unlocked')
  }

  async function importBackup(file: File) {
    if (file.size > 5_000_000) throw new Error('备份文件不能超过 5 MB。')
    const parsed = JSON.parse(await file.text()) as unknown
    validateEnvelope(parsed)
    if (envelope && !window.confirm('导入会替换当前本机保险库。请确认已经保存现有备份。')) return
    await saveEnvelope(parsed)
    setEnvelope(parsed)
    setSession(null)
    setMode('locked')
    setSettingsOpen(false)
    setAnnouncement('备份已导入，请使用对应的主密码解锁。')
  }

  async function commitPayload(payload: VaultPayload) {
    if (!session) throw new Error('保险库已经锁定。')
    const nextEnvelope = await encryptVault(payload, session.key, session.kdf)
    await saveEnvelope(nextEnvelope)
    setEnvelope(nextEnvelope)
    setSession({ ...session, payload })
  }

  async function saveProject(project: ProjectRecord) {
    if (!session) return
    const exists = projects.some((item) => item.id === project.id)
    await commitPayload({ ...session.payload, projects: exists ? projects.map((item) => item.id === project.id ? project : item) : [project, ...projects] })
    setSelectedId(project.id)
    setMobileView('workbench')
    setAnnouncement(exists ? '项目已保存。' : '项目已创建。')
  }

  async function updateSelected(changes: Partial<ProjectRecord>) {
    if (!selectedProject) return
    await saveProject({ ...selectedProject, ...changes, updatedAt: new Date().toISOString() })
  }

  async function deleteSelected() {
    if (!session || !selectedProject || !window.confirm(`删除“${selectedProject.name}”？可在底部提示中立即撤销。`)) return
    setDeletedProject(selectedProject)
    await commitPayload({ ...session.payload, projects: projects.filter((project) => project.id !== selectedProject.id) })
    setSelectedId('')
    setAnnouncement('项目已删除。')
  }

  async function undoDelete() {
    if (!session || !deletedProject) return
    await commitPayload({ ...session.payload, projects: [deletedProject, ...projects] })
    setSelectedId(deletedProject.id)
    setDeletedProject(null)
    setAnnouncement('已恢复项目。')
  }

  async function copyPassword() {
    if (!generated || !session) return
    await navigator.clipboard.writeText(generated)
    setAnnouncement('密码已复制。')
    window.clearTimeout(clipboardTimer.current)
    const seconds = session.payload.settings.clearClipboardSeconds
    if (seconds > 0) {
      clipboardTimer.current = window.setTimeout(async () => {
        try {
          if (await navigator.clipboard.readText() === generated) await navigator.clipboard.writeText('')
        } catch {
          // Clipboard clearing is best-effort and may be denied by the browser.
        }
      }, seconds * 1000)
    }
  }

  async function applyUpdate() {
    const registration = await navigator.serviceWorker.getRegistration()
    if (!registration?.waiting) {
      window.location.reload()
      return
    }
    navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), { once: true })
    registration.waiting.postMessage('SKIP_WAITING')
  }

  if (mode === 'loading') return <main className="loading-screen"><BrandMark /><p>正在读取本机保险库…</p></main>
  if (mode === 'setup' || mode === 'locked') {
    return <AccessScreen hasVault={mode === 'locked'} onCreate={create} onUnlock={unlock} onImport={importBackup} />
  }
  if (!session || !envelope) return null

  const website = selectedProject ? safeWebsite(selectedProject.website) : ''
  const bits = selectedProject ? estimateBits(selectedProject.policy) : 0

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">跳到主要内容</a>
      <header className="app-header">
        <div className="brand-lockup"><BrandMark /><div><span>Pass Creator</span><small>LOCAL VAULT · ONLINE-FREE</small></div></div>
        <div className="header-actions">
          <span className="vault-status"><i /> 已解锁</span>
          <button className="secondary-button desktop-action" type="button" onClick={() => setSettingsOpen(true)}>设置</button>
          <button className="lock-button" type="button" onClick={lock}>锁定</button>
        </div>
      </header>

      {updateReady && <div className="update-banner" role="status"><span>Pass Creator 有新版本可用。</span><button type="button" onClick={applyUpdate}>立即更新</button></div>}

      <div className="workspace">
        <aside className={`project-rail ${mobileView === 'projects' ? 'mobile-active' : ''}`} aria-label="项目列表">
          <div className="rail-heading"><div><p className="section-kicker">保险库</p><h2>项目</h2></div><button className="square-button" type="button" aria-label="新建项目" onClick={() => setProjectDialog('new')}>＋</button></div>
          <label className="search-field"><span className="sr-only">搜索项目</span><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="搜索名称、账户或标签" /></label>
          <div className="segmented-control" aria-label="项目状态">
            <button type="button" className={!showArchived ? 'active' : ''} onClick={() => setShowArchived(false)}>使用中</button>
            <button type="button" className={showArchived ? 'active' : ''} onClick={() => setShowArchived(true)}>已归档</button>
          </div>
          <div className="project-list">
            {visibleProjects.length ? visibleProjects.map((project) => (
              <button
                type="button"
                className={`project-item ${selectedProject?.id === project.id ? 'selected' : ''}`}
                key={project.id}
                onClick={() => { setSelectedId(project.id); setMobileView('workbench') }}
              >
                <span className="project-monogram">{project.name.slice(0, 2).toLocaleUpperCase('zh-CN')}</span>
                <span className="project-copy"><strong>{project.name}</strong><small>{project.account || '未设置账户'} · C{project.counter}</small></span>
                <span className="project-notch" aria-hidden="true" />
              </button>
            )) : <div className="rail-empty"><p>{search ? '没有匹配的项目。' : showArchived ? '没有归档项目。' : '还没有项目。'}</p>{!search && !showArchived && <button type="button" onClick={() => setProjectDialog('new')}>创建第一个项目</button>}</div>}
          </div>
          <div className="rail-footer"><span>{projects.filter((project) => !project.archived).length} 个使用中项目</span><button type="button" onClick={() => setSettingsOpen(true)}>管理保险库</button></div>
        </aside>

        <main id="main-content" className={`workbench ${mobileView === 'workbench' ? 'mobile-active' : ''}`}>
          {selectedProject ? (
            <>
              <section className="project-heading">
                <div>
                  <p className="eyebrow">PROJECT KEY / C{selectedProject.counter.toString().padStart(2, '0')}</p>
                  <h1>{selectedProject.name}</h1>
                  <p>{selectedProject.account || '未设置账户标识'}{website && <> · <a href={website} target="_blank" rel="noreferrer">访问网站</a></>}</p>
                </div>
                <button className="secondary-button" type="button" onClick={() => setProjectDialog('edit')}>编辑项目</button>
              </section>

              <section className="key-bench" aria-labelledby="current-password-title">
                <div className="bench-label"><div><p className="section-kicker">生成结果</p><h2 id="current-password-title">当前密码</h2></div><span className="strength-badge"><i /> 约 {bits} bits</span></div>
                <div className="key-strip" aria-label={revealed ? `当前密码：${generated}` : '当前密码已隐藏'}>
                  {generated ? [...generated].map((character, index) => (
                    <span key={`${selectedProject.id}-${selectedProject.counter}-${index}`} data-kind={characterKind(character)} aria-hidden="true">{revealed ? character : '•'}</span>
                  )) : <span className="key-loading">生成中…</span>}
                </div>
                <div className="key-actions">
                  <button className="primary-button" type="button" onClick={copyPassword} disabled={!generated}>复制密码</button>
                  <button className="secondary-button" type="button" onClick={() => setRevealed((value) => !value)} disabled={!generated}>{revealed ? '隐藏' : '显示'}</button>
                  <button className="secondary-button" type="button" onClick={() => window.confirm('轮换会生成一个新密码。请先确认目标账户允许你更改密码。') && updateSelected({ counter: selectedProject.counter + 1 })}>轮换 · C{selectedProject.counter + 1}</button>
                </div>
                <p className="bench-note">密码不会保存；每次都由加密种子、项目规则和轮换计数重新计算。</p>
              </section>

              <section className="workbench-grid">
                <div className="instrument-panel">
                  <div className="panel-heading"><div><p className="section-kicker">规则刻度</p><h2>密码结构</h2></div><span>V1</span></div>
                  <RuleEditor project={selectedProject} onSave={(policy) => updateSelected({ policy })} />
                </div>
                <aside className="project-details">
                  <div className="panel-heading"><div><p className="section-kicker">项目记录</p><h2>上下文</h2></div></div>
                  {selectedProject.tags.length > 0 && <div className="tag-list">{selectedProject.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>}
                  <dl>
                    <div><dt>轮换计数</dt><dd>C{selectedProject.counter}</dd></div>
                    <div><dt>最近修改</dt><dd>{new Intl.DateTimeFormat('zh-CN', { dateStyle: 'medium' }).format(new Date(selectedProject.updatedAt))}</dd></div>
                  </dl>
                  <p className={selectedProject.notes ? 'project-notes' : 'project-notes empty'}>{selectedProject.notes || '没有备注。可以记录特殊限制，但不要写入明文密码。'}</p>
                  <div className="danger-actions">
                    <button type="button" onClick={() => updateSelected({ archived: !selectedProject.archived })}>{selectedProject.archived ? '恢复项目' : '归档项目'}</button>
                    <button type="button" className="danger-link" onClick={deleteSelected}>删除项目</button>
                  </div>
                </aside>
              </section>
            </>
          ) : (
            <section className="empty-workbench">
              <BrandMark />
              <p className="eyebrow">EMPTY WORKBENCH</p>
              <h1>建立第一条项目规则</h1>
              <p>项目名称与账户标识会成为稳定输入。密码本身不会写入保险库。</p>
              <button className="primary-button" type="button" onClick={() => setProjectDialog('new')}>新建项目</button>
            </section>
          )}
        </main>
      </div>

      <nav className="mobile-nav" aria-label="主要导航">
        <button className={mobileView === 'projects' ? 'active' : ''} type="button" onClick={() => setMobileView('projects')}>项目</button>
        <button className={mobileView === 'workbench' ? 'active' : ''} type="button" onClick={() => setMobileView('workbench')}>工作台</button>
        <button type="button" onClick={() => setSettingsOpen(true)}>设置</button>
      </nav>

      <div className="sr-only" aria-live="polite">{announcement}</div>
      {deletedProject && <div className="toast" role="status"><span>“{deletedProject.name}”已删除</span><button type="button" onClick={undoDelete}>撤销</button><button type="button" aria-label="关闭提示" onClick={() => setDeletedProject(null)}>×</button></div>}
      {projectDialog && <ProjectDialog project={projectDialog === 'edit' ? selectedProject ?? undefined : undefined} onClose={() => setProjectDialog(null)} onSave={saveProject} />}
      {settingsOpen && <SettingsDialog
        settings={session.payload.settings}
        envelope={envelope}
        onClose={() => setSettingsOpen(false)}
        onSave={(settings) => commitPayload({ ...session.payload, settings })}
        onImport={importBackup}
        onChangeMaster={async (password) => {
          const changed = await changeMasterPassword(session.payload, password)
          await saveEnvelope(changed.envelope)
          setEnvelope(changed.envelope)
          setSession(changed.session)
        }}
      />}
    </div>
  )
}
